import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { LicenseService } from '@shared/services/license.service';
import { NGXLogger } from 'ngx-logger';
import { Observable, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class LicenseGuard {
  public constructor(
    protected router: Router,
    protected authService: AuthService,
    protected licenseService: LicenseService,
    protected userService: UserService,
    protected logger: NGXLogger
  ) {}

  private async hasToken() {
    const token = await this.authService.getToken();
    if (!token) {
      this.logger.error(LicenseGuard.name + ' Failed to get token');
      await this.router.navigate(['401']);
    }
    return !!token;
  }

  protected async canActivateInternal(appModule: string | undefined, checkUserAccess = true, redirect = false) {
    const hasToken = await this.hasToken();

    if (!hasToken) {
      return false;
    }

    if (!appModule) {
      const license = await firstValueFrom(this.licenseService.getLicense().pipe(take(1)));
      if (!license && redirect) {
        await this.redirect();
      }
      return !!license;
    }

    const status = await firstValueFrom(this.licenseService.isLicenseValid(appModule).pipe(take(1)));

    if (status === 'module-not-found' || status === 'no-license') {
      if (redirect) {
        await this.redirect();
      }
      return false;
    }
    if (status === 'expired') {
      if (redirect) {
        await this.redirect('/license-expired');
      }
      return false;
    }

    const isTooManyUsers = await this.licenseService.isTooManyUsersForModule(appModule, false);
    if (isTooManyUsers) {
      if (redirect) {
        await this.redirect();
      }
      return false;
    }

    const statusCheck: boolean = status === 'valid';

    if (!appModule || !statusCheck || !checkUserAccess) return statusCheck;

    const isUserAllowed = await firstValueFrom(
      this.licenseService.isUserAllowedToAccessModule(appModule).pipe(take(1))
    );

    if (!isUserAllowed && redirect) {
      await this.redirect();
    }

    return isUserAllowed;
  }

  protected async redirect(to = '/unauthorized') {
    const isAppAdmin = await this.userService.hasRoleAsync('app-admin');
    if (isAppAdmin) {
      await this.router.navigate(['/app/settings-management', 'license']);
    } else {
      await this.router.navigate([to]);
    }
  }

  public canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.canActivateInternal(next.data.appModule, next.data.checkUserAccess, true);
  }

  public canActivateChild(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.canActivate(next, state);
  }

  public canMatch(route: Route, segments: UrlSegment[]): Observable<boolean> | Promise<boolean> | boolean {
    return this.canActivateInternal(route.data.appModule, route.data.checkUserAccess, false);
  }
}
